import type { TCell, TData } from "@package/grid-utils";
import { useDeepCompareMemo } from "@react-hookz/web";
import type { ColDef } from "ag-grid-community";
import { useEffect } from "react";

import {
  type TProduct,
  useActivePageId,
  usePage,
  usePageProducts,
  useProductConfigs,
  useProductsByIds,
  useGridSettingsSelect,
  initGridSettings,
  useGridSettingsStatusMap,
  useProductUserSettings,
  usePackagesDp,
} from "../../../data";
import type { PageProductsFragmentFragment } from "../../../__generated__/gql/graphql";
import { useProcessCellStyle } from "../cell-style/useProcessCellStyle";
import { useAdhocSpreads } from "../../../data/pages/timespreads/hooks";
import { useGetProductsContextMenu } from "../context-menu";
import { genColumnDefs } from "./columnDefs";
import { normalizeMainColumnType } from "@package/grid-utils";
import { useGrid } from "../stores";

export { mainMonthColumnId } from "@package/grid-utils";
export {
  genColumnDefs,
  formatMainMonthPeriod,
  curveColumnStack,
  validColumnSettings,
  defaultColumnSettings,
  potentiallyInvisibleColumns,
  type CurveColumnStackName,
} from "./columnDefs";

export const useGridColumns = (): [isLoading: boolean, columns: ColDef<TData, TCell>[]] => {
  const pageId = useActivePageId();
  const page = usePage(pageId);
  const pageProducts = usePageProducts(pageId);
  const spreads = useAdhocSpreads(pageId);
  const configs = useProductConfigs();
  const statusMap = useGridSettingsStatusMap();
  const productIds = (pageProducts.data ?? []).map((p) => p.product_id).filter(Boolean);
  const products = useProductsByIds(productIds);
  const productSettings = useProductUserSettings();
  const packagesDp = usePackagesDp();
  const mainMonthColumnWidth = useGridSettingsSelect((x) => x?.main_month_column_width);
  const mainColumnType = normalizeMainColumnType(page.data?.main_column_type);
  const displayNegativeBracket = useGridSettingsSelect((x) => !!x?.display_negative_bracket);
  const flashCellUpdates = useGridSettingsSelect((x) => !!x?.flash_cell_updates);
  const mainMenuItems = useGetProductsContextMenu();

  const currentPageProducts = (pageProducts.data ?? []).map((p) => {
    const info = products.data?.find((x) => x.id === p.product_id);
    return { ...info, ...p };
  }) as (PageProductsFragmentFragment & TProduct)[];

  const processCellStyle = useProcessCellStyle();
  const [gridApi] = useGrid();

  // Fixes EOD status not existing when page is first loaded.
  useEffect(() => {
    if (gridApi && products.data) {
      gridApi.refreshHeader();
    }
  }, [gridApi, products.data]);

  useEffect(() => {
    if (gridApi && mainColumnType) {
      gridApi.refreshHeader();
    }
  }, [gridApi, mainColumnType]);

  const isLoading =
    products.isLoading ||
    configs.isLoading ||
    pageProducts.isLoading ||
    productSettings.isLoading ||
    packagesDp.isLoading;

  const defs = useDeepCompareMemo(() => {
    if (!products.data) return [];
    const colDefs = genColumnDefs({
      currentPageProducts,
      statusMap,
      products: products.data ?? [],
      spreads: spreads.data ?? {},
      // Layered / migration fallback: prefer the page-level width, then the legacy grid-settings width, then the built-in default.
      mainMonthColumnWidth:
        page.data?.main_column_width ?? mainMonthColumnWidth.data ?? initGridSettings.main_month_column_width,
      mainColumnType,
      displayNegativeBracket: !!displayNegativeBracket.data,
      flashCellUpdates: !!flashCellUpdates.data,
      productSettings,
      packagesDp,
      configs: configs.data ?? {},
      processCellStyle,
    });

    return colDefs.map((col) => {
      const divCol = ["month", "month-timespread", "week", "blank"].includes(col.field ?? "");
      if (divCol) return col;
      return { ...col, mainMenuItems };
    });
  }, [
    spreads,
    currentPageProducts,
    statusMap,
    products.data,
    productSettings.data,
    packagesDp?.data,
    mainMonthColumnWidth.data,
    page.data?.main_column_width,
    mainColumnType,
    flashCellUpdates.data,
    displayNegativeBracket.data,
    configs.data,
  ]);

  return [isLoading, defs];
};
